import { useId, type HTMLAttributes, type Ref } from 'react'
import './stat-field.scss'

interface StatFieldProps {
  label: string
  hint?: string
  required?: boolean
  value: string
  placeholder?: string
  maxLength?: number
  showCounter?: boolean
  inputMode?: HTMLAttributes<HTMLInputElement>['inputMode']
  error?: string
  inputRef?: Ref<HTMLInputElement>
  onChange: (value: string) => void
  onBlur: () => void
}

export function StatField({
  label,
  hint,
  required = false,
  value,
  placeholder,
  maxLength,
  showCounter = false,
  inputMode,
  error,
  inputRef,
  onChange,
  onBlur,
}: StatFieldProps) {
  const uid = useId()
  const inputId = `${uid}-input`
  const hintId = `${uid}-hint`
  const errorId = `${uid}-error`

  const describedBy =
    [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined

  const inputClassName = [
    'form-control',
    'stat-field__input',
    error ? 'stat-field__input--invalid' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className='stat-field'>
      <div className='stat-field__label-row'>
        <label className='stat-field__label' htmlFor={inputId}>
          {label}
          {required && <span aria-hidden='true'>*</span>}
        </label>
        {hint && (
          <span className='stat-field__hint' id={hintId}>
            {hint}
          </span>
        )}
      </div>

      <input
        id={inputId}
        ref={inputRef}
        className={inputClassName}
        type='text'
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        inputMode={inputMode}
        autoComplete='off'
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
      />

      <div className='stat-field__footer'>
        {error ? (
          <p className='stat-field__error' id={errorId} role='alert'>
            {error}
          </p>
        ) : (
          <span />
        )}
        {showCounter && maxLength !== undefined && (
          <span className='stat-field__counter' aria-hidden='true'>
            {value.length}/{maxLength}
          </span>
        )}
      </div>
    </div>
  )
}
